<template>
  <div
    class="filter-or-criteria"
    :class="{'filter-inputs-lg': isFullWidth}"
  >
    <div
      class="p-r-20 slide-down"
    >
      <div
        v-if="orIndex > 0"
        class="m-t-10 m-b-15 text-center filter-or-criteria__or-text"
      >
        <strong
          v-t="'ROUTER_FILTER_OR'"
          class="m-l-15"
        />
      </div>

      <div v-if="!isReadOnly && isEditView">
        <div
          class="filter-inputs"
          :class="{ 'filter-item-geo': isGeoTag }"
        >
          <!-- Tag selector - START -->
          <SearchableDropdown
            v-if="!isFullWidth || isMobileView"
            v-model="criteriaTag"
            :config="tagsOptions"
            :placeholder="$t('ROUTER_TAG_SELECT_PH')"
            rules="required"
            required
          />

          <ValidationProvider
            rules="required"
            slim
          >
            <TagSelector
              v-if="isFullWidth && !isMobileView"
              v-model="criteriaTag"
              class="filter-tag-id"
              :tags="availableTags"
              :form-name="formNameForUserSettings"
              @change="onTagIdChange"
            />
          </ValidationProvider>
          <!-- Tag selector - END -->
    
          <SearchableDropdown
            v-model="criteriaComparisonType"
            :config="comparisonOptions"
            :placeholder="$t('ROUTER_TAG_SELECT_PH')"
            class="filter-comparison-type"
            rules="required"
            @change="handleComparisonTypeChange"
          />
    
          <CriteriaValueInput
            v-if="criteriaComparisonType.key != 'EXISTS'"
            v-model="criteriaValue"
            :is-full-width="isFullWidth"
            :criteria="criteria"
            :bulk-tags-uploads="bulkTagUploads"
          />
        </div>
        <a
          class="btn btn-function filter-or-criteria__remove" 
          @click="removeRule()"
        >
          <i class="fa fa-times" />
        </a>
      </div>

      <OrCriteriaSummary
        v-if="!isEditView || isReadOnly"
        :criteria="orCriteria"
        :is-read-only="isReadOnly"
        :available-tags="availableTags"
        @edit="enableEditView"
      />
    </div>
  </div>
</template>

<script>
import { isEqual } from 'lodash';

import { comparisonTypes, geoTagsIds, geoComparisonTypes } from '@/data/filterData';
import messages from './locales';
import sharedMessages from '@/core/locales';
import TagSelector from '@/core/components/TagSelector/TagSelector';
import SearchableDropdown from '@/core/components/SearchableDropdown/SearchableDropdown';
import OrCriteriaSummary from './OrCriteriaSummary/OrCriteriaSummary';
import { camelCaseFormat } from '@/core/filters/camelCaseFormat';
import CriteriaValueInput from './CriteriaValueInput.vue';
import { mapState } from 'vuex';
import { utilities } from '@/angularBridgeCode/ringbaPortal.servicesModule';


export default {
  name: 'OrCriteria',

  components: { 
    TagSelector, 
    SearchableDropdown, 
    OrCriteriaSummary, 
    CriteriaValueInput 
  },

  props: {
    orCriteria: {
      type: Object,
      default() { 
        return {
          tagId: '', 
          comparisonTypeTemp: {
            key: 'EQUALS',
            value: 'EQUALS_SINGLE_VALUE',
            isNegativeMatch: false,
            forGeoTags: false,
            forMultipleValues: false,
          }
        }; 
      }
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isFullWidth: {
      type: Boolean,
      default: false
    },
    isSummaryView: {
      type: Boolean,
      default: false
    },
    isMobileView: {
      type: Boolean,
      default: false
    },
    orIndex: {
      type: Number,
      default: -1
    },
    andIndex: {
      type: Number,
      default: -1
    },
    formNameForUserSettings: {
      type: String,
      default: ''
    },
    availableTags: {
      type: Array,
      default() { 
        return []; 
      }
    },

    hasBulkComparisonType: {
      type: Boolean,
      default: false
    },

    bulkComparisonIndex: {
      type: Number,
      default: -1
    }
  },
  data() {
    return {
      isEditView: !this.isSummaryView,
      criteria: this.orCriteria,
      bulkTagsUploads: []
    };
  },
  computed: {
    ...mapState('callFlows', ['bulkTagUploads']),

    criteriaComparisonType: {
      get() { 
        return this.orCriteria.comparisonTypeTemp; 
      },
      set(value) {
        this.criteria.comparisonTypeTemp = value;
        this.criteria.comparisonType = value.key;
        this.criteria.isNegativeMatch = value.isNegativeMatch;
        this.criteria.isGeoTag = value.forGeoTags;
        this.criteria.forBulkTags = value.forBulkTags;
        this.criteria.forMultipleValues = value.forMultipleValues;
        this.criteria.useNumericalValue = value.useNumericalValue;
        this.criteria.value = '';
        this.handleCriteriaChange();
      }
    },
    criteriaValue: {
      get() { 
        return this.orCriteria.value; 
      },
      set(value) {
        this.criteria.value = value;
        this.handleCriteriaChange();
      }
    },

    criteriaTag: {
      get() {
        if (this.criteria.tagId) {
          const tagId = this.criteria.tagId;

          return this.availableTags.find((tag) => {
            return this.getTagId(tag) === tagId;
          });
        }
        return null;
      },
      set(tag) {
        this.criteria.tagId = this.getTagId(tag);
        this.onTagIdChange();
        this.handleCriteriaChange();
      }
    },

    comparisonOptions() {
      return {
        options: this.isGeoTag ? [...geoComparisonTypes] : [...comparisonTypes.filter(c => !c.forGeoTags)],
        placeholder: this.$i18n.t('ROUTER_TAG_SELECT_PH'),
        getOptionKey: function getComparisonOptionKey(option) {
          return option.value;
        },
        getOptionLabel: function getOptionLabel(option) {
          return option && camelCaseFormat(option.value);
        },
        clearable: false
      };
    },

    tagsOptions() {
      return {
        options: this.availableTags,
        placeholder: this.$i18n.t('ROUTER_TAG_SELECT_PH'),
        getOptionLabel: function getTagOptionLabel(option) {
          return option && `${camelCaseFormat(option.tagSource)} - ${camelCaseFormat(option.tagType)} - ${camelCaseFormat(option.tagName)}`;
        },
        clearable: false
      };
    },

    isGeoTag() {
      return !!geoTagsIds.find((tag) => {
        return tag.tagId === this.criteria.tagId;
      });
    },
  },
  watch: {
    orCriteria: {
      immediate: true,
      handler(newValue, oldValue) {
        if(!isEqual(newValue, oldValue)) {
          this.criteria = newValue;
        }
      }
    },
    isSummaryView() {
      this.isEditView = !this.isSummaryView;
    }
  },

  methods: {
    handleComparisonTypeChange(comparison) {
      if (comparison.forBulkTags && this.hasBulkComparisonType && this.orIndex !== this.bulkComparisonIndex) {
        utilities.showToaster('error', 'You cannot add multiple bulk tags in a single logic group filter.');

        setTimeout(() => {
          this.criteriaComparisonType = comparisonTypes.find(c => c.value === 'EQUALS_SINGLE_VALUE' && !c.isNegativeMatch);
        }, 20);
      }
    },

    enableEditView() {
      this.isEditView = true;
    },

    removeRule() {
      this.$emit('removeRule', this.orIndex);
    },

    getTagId(tag) {
      return tag.tagType + ':' + tag.tagName;
    },

    handleCriteriaChange() {
      this.$emit('change', this.criteria);
    },

    onTagIdChange() {
      if (this.isGeoTag) {
        this.criteriaValue = [];
        this.criteriaComparisonType = {...geoComparisonTypes[0] };
      } else {
        this.criteriaValue = '';
        this.criteriaComparisonType = {...comparisonTypes.find(c => c.defaultComparisonType)};
      }
    }
  },
  i18n: {
    messages,
    sharedMessages,
    locale: 'en'
  }
};
</script>

<style lang="scss" scoped>
$route-filter-large-and-or-text: 1.4em;

.filter-or-criteria {
  position: relative;

  &:not(:first-of-type) {
    .filter-or-criteria__remove {
      top: 46px;
    }
  }
}

.filter-or-criteria__or-text {
  color: var(--component-ivr-router-filter__or-text-font-color);
  
  .filter-inputs-lg & {
    font-size: $route-filter-large-and-or-text;
  }
}

.filter-or-criteria__remove {
  position: absolute;
  right: -5px;
  top: 10px;
}

.filter-or-criteria__value {
  flex-grow: 1;
}

  @media (min-width: $screen-md-min) {
    .filter-inputs-lg {
      .filter-inputs {
        display: flex;
        align-items: flex-start;

        &.filter-item-geo {
          .filter-tag-id {
            flex: 0 0 32.7%;
          }

          .filter-comparison-type {
            .dropdown-menu {
              top: auto;
            }
          }
        }

        .filter-tag-id {
          flex: 0 0 47%;
        }

        .filter-comparison-type {
          flex: 0 0 19%;
          min-width: 19%;
          margin: 0 15px;
        }

        .filter-comparison-type__selected {
          @include text-overflow();
          max-width: 100px;
        }

        .filter-criteria-value__dropdown {
          max-width: 200px;
        }

        .filter-criteria-value {
          flex: 0 0 30%;
        }
      }
    }
  }

  @media (min-width: 1400px) {
    .filter-inputs-lg {
      .bulk-tag-selected {
        max-width: 200px;
      }

      .filter-inputs {
        .filter-comparison-type__selected {
          max-width: 200px;
        }

        .filter-criteria-value__dropdown {
          max-width: 230px;
        }
      }
    }
  }

  @media (min-width: 1900px) {
    .filter-inputs-lg {
      .filter-inputs {
        .filter-criteria-value__dropdown {
          max-width: 300px;
        }
      }
    }
  }
</style>